import moment from 'moment';
import GoogleAnalytics from 'react-ga';

import fc from 'businessLogic/fetchInterceptor';
import { loggedIn } from 'businessLogic/authorizator.js';
import { displayGlobalMessage } from 'actions/globalMessage';
import { getUserBasicProfile } from './user';
import { getMeetingRoomsByPage } from './meetingRoom';
import { subscribeForHeartbeat, reSubscribeMissingPubnubChannels } from 'actions/pubnub/init';
import { getWaitingAreasDetails } from 'businessLogic/pubnubHelpers';
import { queueESEvents, ESEventTypes } from 'businessLogic/logEventToElasticSearch';

export const PUBNUB_HEALTH_STATE = {
  CLIENT: 'PUBNUB_HEALTH_STATE.CLIENT',
  SERVER: 'PUBNUB_HEALTH_STATE.SERVER',
  HEARTBEAT_RECEVIED: 'PUBNUB_HEALTH_STATE.HEARTBEAT_RECEVIED',
  SECONDS_TO_AUTO_REFRESH: 'PUBNUB_HEALTH_STATE.SECONDS_TO_AUTO_REFRESH',
  SECONDS_SINCE_PUBNUB_DOWN: 'PUBNUB_HEALTH_STATE.SECONDS_SINCE_PUBNUB_DOWN',
};

export function requestHeartbeat() {
  if (loggedIn()) {
    console.log('Requesting personal pubnub heartbeat...');
    // request personal heartbeat if user is still logged in
    const apiUrl = window.APP.CONFIGURATION.VCCAPI + 'users/RequestPubnubHeartbeat';
    fc.get(apiUrl).catch((err) => {
      console.error(`There was an API error calling ${apiUrl}`, err);
    });
  }
}

export function tryReconnectPubNub() {
  if (window.pubnub) {
    const channelsReconnected = reSubscribeMissingPubnubChannels(false);
    if (channelsReconnected === 0) {
      console.log(
        'Pubnub status is unhealthy and no channels found missing for resubscription, hence reconnecting to Pubnub...'
      );
      // https://www.pubnub.com/docs/web-javascript/api-reference-misc#reconnect
      window.pubnub.reconnect();
    }

    setTimeout(() => {
      requestHeartbeat();
    }, 1000);
  }
}

export function startPubnubHealthMonitor(dispatcher) {
  subscribeForHeartbeat(dispatcher);
  monitorBrowserNetworkStatus(dispatcher);
  monitorPubnubServerStatus(dispatcher);
  monitorUpdatesWhenPubnubDown(dispatcher);
}

export function stopPubnubHealthMonitor(dispatcher) {
  // Reset Pubnub to healthy and stop the check timers
  dispatcher(setPubnubServerHealth(true));
  dispatcher(setSecondsToAutoRefresh(null));
  dispatcher(setSecondsSincePubnubDown(null));
  window.pubnubHeartbeatHealthCheckHandle && window.clearInterval(window.pubnubHeartbeatHealthCheckHandle);
  window.monitorUpdatesWhenPubnubDownHandle && window.clearInterval(window.monitorUpdatesWhenPubnubDownHandle);
}

function monitorBrowserNetworkStatus(dispatcher) {
  // check if browser goes offline
  window.addEventListener('offline', (event) => updateBrowserNetworkStatus(event, dispatcher));

  // check if browser goes online
  window.addEventListener('online', (event) => updateBrowserNetworkStatus(event, dispatcher));
}

function updateBrowserNetworkStatus(event, dispatcher) {
  var condition = navigator.onLine ? 'online' : 'offline';

  console.log('Browswer network connection status changed :', condition);

  if (condition === 'online') {
    // browser is back online, reload page for a full refresh
    window.location.reload();
  } else {
    // browser is offline, show error message
    var error = APP.LANGUAGE.Errors.OfflineMessage.replace('##portalName##', window.APP.CONFIGURATION.PORTAL.Name);
    dispatcher(
      displayGlobalMessage({
        text: error,
        visible: true,
        messageType: APP.LANGUAGE.GlobalMessageTypes.Bad,
        autoHide: false,
        alwaysOnTop: true,
        browserURL: window.location.href,
      })
    );
  }
}

function monitorPubnubServerStatus(dispatcher) {
  if (window.pubnubHeartbeatHealthCheckHandle) {
    console.log(
      'pubnubHeartbeatHealthCheckHandle already running, so cancel this one before starting new setInterval',
      window.pubnubHeartbeatHealthCheckHandle
    );
    window.clearInterval(window.pubnubHeartbeatHealthCheckHandle);
  }

  // every X mins check the health of the pubnub heartbeat
  const pubnubHeartbeatHealthCheckTimerSeconds = window.APP.CONFIGURATION.PUBNUB_HEALTH_CHECK_TIMER_SECS || 125;
  window.pubnubHeartbeatHealthCheckHandle = setInterval(function () {
    // check if last hearbeat was received since last check
    const currentEpocTimeSeconds = moment.utc().unix();
    const lastHeartbeatUtc = window.getState().sharedReducer.pubnubConnectionStatus.lastHeartbeatUtc;
    const secondsSinceLastHeartbeat = currentEpocTimeSeconds - lastHeartbeatUtc;

    if (!lastHeartbeatUtc) {
      // no heartbeat ever received, set health status to be dirty
      console.log(
        'Pubnub heartbeat never received, set health status to be dirty',
        lastHeartbeatUtc,
        !lastHeartbeatUtc
      );
      dispatcher(setPubnubServerHealth(false));
      tryReconnectPubNub();
      // TODO - Log this into New Relic instead (after 3.9.1 is out)
      window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
        GoogleAnalytics.event({
          category: 'VCM',
          action: `Pubnub heartbeat never received`,
        });
      queueESEvents(
        ESEventTypes.PubNubHeartbeatReceived,
        `Not received. lastHeartbeatUtc=${lastHeartbeatUtc}`,
        'eventType'
      );
    } else if (secondsSinceLastHeartbeat > pubnubHeartbeatHealthCheckTimerSeconds) {
      console.log(
        `Pubnub heartbeat received more than ${pubnubHeartbeatHealthCheckTimerSeconds} seconds ago, hard limit reached, set health status to be dirty`,
        currentEpocTimeSeconds,
        lastHeartbeatUtc,
        pubnubHeartbeatHealthCheckTimerSeconds,
        secondsSinceLastHeartbeat
      );
      dispatcher(setPubnubServerHealth(false));
      tryReconnectPubNub();
      // TODO - Log this into New Relic instead (after 3.9.1 is out)
      window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
        GoogleAnalytics.event({
          category: 'VCM',
          action: `Pubnub heartbeat hard limit reached`,
          label: `secondsSinceLastHeartbeat=${secondsSinceLastHeartbeat}`,
        });

      queueESEvents(
        ESEventTypes.PubNubHeartbeatHardLimitReach,
        `secondsSinceLastHeartbeat=${secondsSinceLastHeartbeat}`
      );
    } else if (secondsSinceLastHeartbeat > pubnubHeartbeatHealthCheckTimerSeconds / 2) {
      console.log(
        `Pubnub heartbeat received more than ${
          pubnubHeartbeatHealthCheckTimerSeconds / 2
        } seconds ago, soft limit reached, try to recover`,
        currentEpocTimeSeconds,
        lastHeartbeatUtc,
        pubnubHeartbeatHealthCheckTimerSeconds,
        secondsSinceLastHeartbeat
      );
      tryReconnectPubNub();
      // TODO - Log this into New Relic instead (after 3.9.1 is out)
      window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
        GoogleAnalytics.event({
          category: 'VCM',
          action: `Pubnub heartbeat soft limit reached`,
          label: `secondsSinceLastHeartbeat=${secondsSinceLastHeartbeat}`,
        });
      queueESEvents(
        ESEventTypes.PubNubHeartbeatSoftLimitReach,
        `secondsSinceLastHeartbeat=${secondsSinceLastHeartbeat}`
      );
    } else {
      console.log(
        `Pubnub heartbeat received with in last ${pubnubHeartbeatHealthCheckTimerSeconds} seconds ago, health status to be healthy`,
        currentEpocTimeSeconds,
        lastHeartbeatUtc,
        pubnubHeartbeatHealthCheckTimerSeconds,
        secondsSinceLastHeartbeat
      );
      dispatcher(setPubnubServerHealth(true));
    }

    // Check if Pubnub thinks if its connected
    // https://www.pubnub.com/docs/web-javascript/pubnub-javascript-sdk#time
    // TODO - After 3.9.1 measure the count of this logging to work out next actions
    window.pubnub &&
      window.pubnub.time(function (status, response) {
        if (status.error) {
          console.error('Pubnub is NOT online', status, response);
          // handle error if something went wrong based on the status object
          window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
            GoogleAnalytics.event({
              category: 'VCM',
              action: 'window.pubnub.time failed',
              label: `secondsSinceLastHeartbeat=${secondsSinceLastHeartbeat}`,
            });
        } else {
          console.log('Pubnub is online, response.timetoken :', response.timetoken);
        }
      });

    // log any missing channel to Google Analytics without attempting to re-subscribe
    reSubscribeMissingPubnubChannels(true);
  }, (pubnubHeartbeatHealthCheckTimerSeconds * 1000) / 2); // if pubnubHeartbeatHealthCheckTimerSeconds is 125, then run every 62.5 seconds
}

export function setPubnubClientHealth(isHealthy) {
  if (isHealthy && !window.getState().sharedReducer.pubnubConnectionStatus.isClientHealthy) {
    // if punub client connection has recovered, reload page for a full data refresh
    window.location.reload();
    return;
  }

  return (dispatch) =>
    dispatch({
      type: PUBNUB_HEALTH_STATE.CLIENT,
      data: isHealthy,
    });
}

export function setPubnubServerHealth(isHealthy) {
  console.log('setPubnubServerHealth called', isHealthy);

  if (isHealthy && !window.getState().sharedReducer.pubnubConnectionStatus.isServerHealthy) {
    // if punub server connection has recovered, reload page for a full data refresh
    window.location.reload();
    return;
  }

  return (dispatch) =>
    dispatch({
      type: PUBNUB_HEALTH_STATE.SERVER,
      data: isHealthy,
    });
}

export function setSecondsToAutoRefresh(value) {
  return (dispatch) =>
    dispatch({
      type: PUBNUB_HEALTH_STATE.SECONDS_TO_AUTO_REFRESH,
      data: value,
    });
}

export function setSecondsSincePubnubDown(value) {
  return (dispatch) =>
    dispatch({
      type: PUBNUB_HEALTH_STATE.SECONDS_SINCE_PUBNUB_DOWN,
      data: value,
    });
}

export function updatePubNubClientStatus(isHealthy, dispatcher) {
  dispatcher(setPubnubClientHealth(isHealthy));
}

export function setPubnubHeartbeatReceived(lastHeartbeatUtc, dispatcher) {
  // set server status to be healthy
  dispatcher(setPubnubServerHealth(true));

  // update last heartbeat time to be procossed the monitoring process
  return (dispatch) =>
    dispatch({
      type: PUBNUB_HEALTH_STATE.HEARTBEAT_RECEVIED,
      data: lastHeartbeatUtc,
    });
}

function monitorUpdatesWhenPubnubDown(dispatcher) {
  if (window.monitorUpdatesWhenPubnubDownHandle) {
    return;
  }

  // run every 1 seconds
  const monitorUpdatesWhenPubnubDownTimerSeconds = 1;
  window.monitorUpdatesWhenPubnubDownHandle = setInterval(function () {
    const pubnubConnectionStatus = window.getState().sharedReducer.pubnubConnectionStatus;
    if (pubnubConnectionStatus.isClientHealthy && pubnubConnectionStatus.isServerHealthy) {
      // pubnub healthy, clear time to update if required
      if (!!pubnubConnectionStatus.secondsToAutoRefresh) {
        dispatcher(setSecondsToAutoRefresh(null));
      }
      if (!!pubnubConnectionStatus.secondsSincePubnubDown) {
        dispatcher(setSecondsSincePubnubDown(null));
      }
    } else {
      // pubnub is unhealthy, set timer to new value
      let newSecondsToAutoRefresh = window.APP.CONFIGURATION.PUBNUB_UHEALTHY_AUTO_DATA_FETCH_TIMER_SECS || 60;
      if (!!pubnubConnectionStatus.secondsToAutoRefresh && pubnubConnectionStatus.secondsToAutoRefresh > -1) {
        newSecondsToAutoRefresh = pubnubConnectionStatus.secondsToAutoRefresh - 1;
      }
      const isTimeToReFetchData = newSecondsToAutoRefresh === 0;
      dispatcher(setSecondsToAutoRefresh(newSecondsToAutoRefresh));

      let newSecondsSincePubnubDown = 0;
      if (pubnubConnectionStatus.secondsSincePubnubDown != null) {
        newSecondsSincePubnubDown = pubnubConnectionStatus.secondsSincePubnubDown + 1;
      }
      dispatcher(setSecondsSincePubnubDown(newSecondsSincePubnubDown));

      const reloadTimerSecs = window.APP.CONFIGURATION.PUBNUB_UHEALTHY_AUTO_PAGE_RELOAD_TIMER_SECS || 900;
      const isTimeToReloadPage = newSecondsSincePubnubDown > 0 && newSecondsSincePubnubDown % reloadTimerSecs === 0;

      if (isTimeToReloadPage) {
        // track how many times hard refresh is happening
        // TODO - Log this into New Relic instead (after 3.9.1 is out)
        window.APP.CONFIGURATION.GOOGLE_ANALYTICS_PROPERTY_ID &&
          GoogleAnalytics.event({
            category: 'VCM',
            action: `Pubnub down for ${reloadTimerSecs} seconds, Reloading`,
          });

        queueESEvents(ESEventTypes.PubNubDownReloading, `${reloadTimerSecs} seconds`);

        window.location.reload();
      } else if (isTimeToReFetchData) {
        dispatcher(getUserBasicProfile());

        queueESEvents(
          ESEventTypes.PubNubUnhealthyAutoReload,
          'Pubnub unhealthy auto page reload times up - fetching data',
          'eventType'
        );
        // fetch meeting rooms or waiting areas only if user has access
        const currentUserProfile = window.getState().usersReducer.userProfile;
        if (currentUserProfile.hasWaitingAreas) {
          queueESEvents(
            ESEventTypes.CallingWADetails,
            `When pubnub isTimeToReFetchData`,
            'eventType&eventValue',
            null,
            null,
            true,
            'info'
          );
          getWaitingAreasDetails(dispatcher);
        }
        if (currentUserProfile.hasMeetingRooms) dispatcher(getMeetingRoomsByPage());
      }
    }

    // If pubnub unhealthy, if ready to fetch else update wait time for fetch
  }, monitorUpdatesWhenPubnubDownTimerSeconds * 1000);
}
